// engine-exception: portal work — the customer's approval inbox + vote, portal-visible projects with completable tasks and their health strip, their appointments, and the public knowledge base; customer-facing views of staff-owned records, not resource screens
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../../engine/api/context';
import { Icon } from '../../engine/design/icons';
import { Button } from '../../engine/design/primitives/Button';
import { Field, Input, Textarea } from '../../engine/design/primitives/Input';
import { Badge, Banner, EmptyState, Loading, PageHeader, Progress } from '../../engine/design/primitives/Misc';
import { RichTextView } from '../../engine/fields/RichText';
import { formatDate, formatDateTime, formatQty } from '../../lib/format';
import { useToast } from '../../engine/design/primitives/Toast';
import { usePortal } from '../PortalContext';
import { portalUrl } from '../client';
import { usePortalItem, usePortalList, usePortalMutation } from '../hooks';
import type { AppointmentRow, ApprovalRow, ArticleRow, PrepayBalance, ProjectDetail, ProjectRow, ProjectTask } from '../types';
import { BackLink, downloadFile, fmtBytes, ListState, LoadMore, StatusPill } from '../ui';

// ── Project health ────────────────────────────────────────────────────────────
const TASK_ORDER = ['done', 'review', 'in_progress', 'blocked', 'todo', 'cancelled'] as const;
const TASK_LABEL: Record<string, string> = { done: 'Done', review: 'In review', in_progress: 'In progress', blocked: 'Blocked', todo: 'To do', cancelled: 'Cancelled' };

/** Task counts by status in a fixed order, and what is waiting on the customer (their tasks still open). */
export function projectHealth(tasks: Pick<ProjectTask, 'status' | 'portal_access' | 'completed_at'>[]) {
  const counts = TASK_ORDER.map((status) => ({ status, label: TASK_LABEL[status]!, n: tasks.filter((t) => t.status === status).length })).filter((c) => c.n > 0);
  const waiting = tasks.filter((t) => t.portal_access === 'complete' && t.status !== 'done' && t.status !== 'cancelled' && !t.completed_at).length;
  return { counts, total: tasks.length, waiting };
}

function HoursRing({ b }: { b: PrepayBalance }) {
  const bought = Number.parseFloat(b.purchased_qty) || 0;
  const left = Math.max(0, Number.parseFloat(b.remaining_qty) || 0);
  const share = bought > 0 ? Math.min(1, left / bought) : 0;
  const r = 15.9155; // circumference 100
  return (
    <div className="pt-ring" role="img" aria-label={`${formatQty(b.remaining_qty)} prepaid hours left of ${formatQty(b.purchased_qty)}`}>
      <svg viewBox="0 0 36 36" width="44" height="44" aria-hidden="true">
        <circle cx="18" cy="18" r={r} className="track" />
        <circle cx="18" cy="18" r={r} className={b.over_drawn ? 'fill neg' : share < 0.2 ? 'fill warn' : 'fill'} strokeDasharray={`${(share * 100).toFixed(2)} 100`} />
      </svg>
      <div><strong>{formatQty(b.remaining_qty)} h</strong><span className="hf-muted"> prepaid left of {formatQty(b.purchased_qty)}</span></div>
    </div>
  );
}

export function ProjectHealth({ tasks, prepay }: { tasks: ProjectTask[]; prepay: PrepayBalance[] }) {
  const h = projectHealth(tasks);
  const hours = prepay.filter((b) => b.unit === 'hours');
  if (h.total === 0 && hours.length === 0) return null;
  return (
    <section className="pt-health" aria-label="Project health">
      {h.total > 0 && (
        <div className="pt-health-bar">
          <div className="bar" role="img" aria-label={h.counts.map((c) => `${c.n} ${c.label.toLowerCase()}`).join(', ')}>
            {h.counts.map((c) => <span key={c.status} className={`seg ${c.status}`} style={{ flexGrow: c.n }} title={`${c.label}: ${c.n}`} />)}
          </div>
          <div className="legend">{h.counts.map((c) => <span key={c.status}><i className={`seg ${c.status}`} />{c.label} {c.n}</span>)}</div>
        </div>
      )}
      {h.waiting > 0 && <Badge tone="warn" dot={false}>{h.waiting} waiting on you</Badge>}
      {hours.map((b) => <HoursRing key={b.prepay_account_id} b={b} />)}
    </section>
  );
}

// ── Appointments ──────────────────────────────────────────────────────────────
const WHERE: Record<AppointmentRow['location'], string> = { remote: 'Remote', onsite: 'On site', phone: 'Phone' };

export function AppointmentsPage() {
  const api = useApi();
  const toast = useToast();
  const [sp, setSp] = useSearchParams();
  const when = sp.get('when') === 'past' ? 'past' : 'upcoming';
  const list = usePortalList<AppointmentRow>('appointments', { when });
  const setWhen = (w: 'upcoming' | 'past') => { const n = new URLSearchParams(sp); if (w === 'past') n.set('when', 'past'); else n.delete('when'); setSp(n, { replace: true }); };
  const ics = (a: AppointmentRow) =>
    void downloadFile(api, `/v1/appointments/${encodeURIComponent(a.id)}/ics`, `appointment-${a.starts_at.slice(0, 10)}.ics`, 'text/calendar').catch((e: unknown) =>
      toast({ kind: 'error', title: e instanceof Error ? e.message : 'Could not download the calendar file' }),
    );
  return (
    <div>
      <PageHeader title="Appointments" subtitle="Visits and calls booked with you" />
      <div className="pt-toolbar" role="tablist" aria-label="When">
        <Button role="tab" aria-selected={when === 'upcoming'} variant={when === 'upcoming' ? 'primary' : 'ghost'} onClick={() => setWhen('upcoming')}>Upcoming</Button>
        <Button role="tab" aria-selected={when === 'past'} variant={when === 'past' ? 'primary' : 'ghost'} onClick={() => setWhen('past')}>Past</Button>
      </div>
      <ListState loading={list.isLoading} error={list.error} count={list.rows.length} empty={{ title: when === 'past' ? 'No past appointments' : 'Nothing booked', icon: 'calendar' }}>
        <div className="pt-cards">
          {list.rows.map((a) => (
            <div key={a.id} className="pt-card">
              <span className="ref">{formatDateTime(a.starts_at)} – {new Date(a.ends_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}</span>
              <span className="title">{a.subject || a.type_name || 'Appointment'}</span>
              <span className="meta">
                <span>{WHERE[a.location]}{a.location !== 'remote' && a.location_detail ? ` · ${a.location_detail}` : ''}</span>
                {a.technician_name && <span>with {a.technician_name}</span>}
                {a.ticket_ref && (a.ticket_id ? <Link to={`/portal/tickets/${a.ticket_id}`}>{a.ticket_ref}</Link> : <span>{a.ticket_ref}</span>)}
              </span>
              <span className="side">
                {a.status !== 'booked' && <StatusPill status={a.status} />}
                {a.status === 'booked' && a.join_url && when === 'upcoming' && <a className="hf-btn primary sm" href={a.join_url} target="_blank" rel="noopener noreferrer">Join</a>}
                {a.status === 'booked' && when === 'upcoming' && <Button size="sm" variant="outline" onClick={() => ics(a)} aria-label={`Add ${a.subject || 'appointment'} to calendar`}><Icon name="calendar" size={14} /> Add to calendar</Button>}
              </span>
            </div>
          ))}
        </div>
        <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onMore={list.loadMore} />
      </ListState>
    </div>
  );
}

// ── Approvals ─────────────────────────────────────────────────────────────────
export function ApprovalsPage() {
  const { can } = usePortal();
  const list = usePortalList<ApprovalRow>('approvals', {}, { enabled: can('approvals.vote'), limit: 100 });
  if (!can('approvals.vote')) return <EmptyState title="Approvals are not available to your portal role" icon="lock" />;
  const waiting = list.rows.filter((a) => a.state === 'pending' && !a.voted);
  const rest = list.rows.filter((a) => !(a.state === 'pending' && !a.voted));
  const card = (a: ApprovalRow) => (
    <Link key={a.id} to={`/portal/approvals/${a.id}`} className="pt-card">
      <span className="ref">{a.ticket_ref ?? a.process_name}{a.step_name ? ` · ${a.step_name}` : ''}</span>
      <span className="title">{a.ticket_summary ?? a.process_name}</span>
      <span className="meta"><span>Requested {formatDate(a.started_at)}</span>{a.timeout_at && a.state === 'pending' && <span>Respond by {formatDate(a.timeout_at)}</span>}</span>
      <span className="side"><StatusPill status={a.state} />{a.voted && a.state === 'pending' && <Badge tone="muted" dot={false}>you voted</Badge>}</span>
    </Link>
  );
  return (
    <div>
      <PageHeader title="Approvals" subtitle={waiting.length > 0 ? `${waiting.length} decision${waiting.length === 1 ? '' : 's'} waiting for you` : 'Nothing is waiting for you'} />
      <ListState loading={list.isLoading} error={list.error} count={list.rows.length} empty={{ title: 'No approval requests', icon: 'shield-check' }}>
        {waiting.length > 0 && (
          <section>
            <h2 className="hf-section-title">Waiting for you</h2>
            <div className="pt-cards">{waiting.map(card)}</div>
          </section>
        )}
        {rest.length > 0 && (
          <section>
            <h2 className="hf-section-title">History</h2>
            <div className="pt-cards">{rest.map(card)}</div>
          </section>
        )}
      </ListState>
    </div>
  );
}

export function ApprovalPage() {
  const { id = '' } = useParams();
  const api = useApi();
  const a = usePortalItem<ApprovalRow>('approvals', id);
  const [comment, setComment] = useState('');
  const vote = usePortalMutation((decision: 'approve' | 'reject') => api.resource('approvals').action('vote', { id, body: { decision, comment: comment.trim() || undefined } }), { invalidate: ['approvals', id], success: (r) => ((r as { state?: string })?.state === 'pending' ? 'Vote recorded' : 'Decision recorded') });
  if (a.isLoading) return <Loading />;
  if (a.error || !a.data) return <EmptyState title={a.error?.status === 404 ? 'Approval not found' : 'Could not load this approval'} hint={a.error?.detail} icon="shield-check" action={<Link className="hf-btn" to="/portal/approvals">Back</Link>} />;
  const d = a.data;
  const mine = d.votes?.find((v) => v.mine && v.decision !== 'delegated');
  const open = d.state === 'pending' && !mine;
  return (
    <div className="pt-main narrow" style={{ padding: 0 }}>
      <BackLink to="/portal/approvals">Approvals</BackLink>
      <PageHeader eyebrow={<span className="hf-mono">{d.ticket_ref ?? d.process_name}</span>} title={d.ticket_summary ?? d.process_name} badge={<span style={{ marginLeft: 8 }}><StatusPill status={d.state} /></span>} subtitle={d.step_name ? `${d.process_name} · ${d.step_name}` : d.process_name} />
      {d.ticket_details && <div className="hf-card pad" style={{ marginBottom: 'var(--s-4)' }}><RichTextView html={d.ticket_details} /></div>}
      {d.ticket_id && <p><Link to={`/portal/tickets/${d.ticket_id}`} className="hf-link">Open the related ticket</Link></p>}
      {d.decision_reason && <Banner tone="info">{d.decision_reason}</Banner>}
      {mine && <Banner tone={mine.decision === 'approve' ? 'pos' : 'warn'} icon="check-circle">You {mine.decision === 'approve' ? 'approved' : 'rejected'} this on {formatDateTime(mine.created_at)}{mine.comment ? ` — “${mine.comment}”` : ''}.</Banner>}
      {open && (
        <div className="hf-card pad hf-stack">
          <Field label="Comment (optional)"><Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} /></Field>
          <div className="hf-row">
            <Button variant="danger" busy={vote.isPending} onClick={() => vote.mutate('reject')} icon="x">Reject</Button>
            <span className="hf-grow" />
            <Button variant="primary" busy={vote.isPending} onClick={() => vote.mutate('approve')} icon="check">Approve</Button>
          </div>
        </div>
      )}
      {(d.votes ?? []).length > 0 && (
        <section>
          <h2 className="hf-section-title">Decisions</h2>
          <dl className="hf-kv">
            {d.votes!.map((v) => (
              <div key={v.id}><dt>{formatDateTime(v.created_at)}</dt><dd><StatusPill status={v.decision === 'approve' ? 'approved' : v.decision === 'reject' ? 'rejected' : v.decision} />{v.mine ? ' (you)' : ''}{v.comment ? ` — ${v.comment}` : ''}</dd></div>
            ))}
          </dl>
        </section>
      )}
    </div>
  );
}

// ── Projects ──────────────────────────────────────────────────────────────────
export function ProjectsPage() {
  const { can } = usePortal();
  const list = usePortalList<ProjectRow>('projects', {}, { enabled: can('projects.view') });
  if (!can('projects.view')) return <EmptyState title="Projects are not available to your portal role" icon="lock" />;
  return (
    <div>
      <PageHeader title="Projects" subtitle="Work in progress for your organisation" />
      <ListState loading={list.isLoading} error={list.error} count={list.rows.length} empty={{ title: 'No projects shared with you', icon: 'kanban' }}>
        <div className="pt-cards">
          {list.rows.map((p) => (
            <Link key={p.id} to={`/portal/projects/${p.id}`} className="pt-card">
              <span className="ref">{p.ref}</span>
              <span className="title">{p.name}</span>
              <span className="meta">{p.target_end_date && <span>Target {formatDate(p.target_end_date)}</span>}{p.task_count != null && <span>{p.done_count ?? 0}/{p.task_count} tasks done</span>}</span>
              <span className="side"><StatusPill status={p.status} />{p.health && <Badge tone={p.health === 'green' || p.health === 'on_track' ? 'pos' : p.health === 'red' || p.health === 'off_track' ? 'neg' : 'warn'} dot>{p.health.replace(/_/g, ' ')}</Badge>}</span>
              {p.progress_percent !== null && p.progress_percent !== undefined && <div style={{ gridColumn: '1 / -1' }}><Progress value={p.progress_percent} title={`${p.progress_percent}% complete`} /></div>}
            </Link>
          ))}
        </div>
        <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onMore={list.loadMore} />
      </ListState>
    </div>
  );
}

export function ProjectPage() {
  const { id = '' } = useParams();
  const api = useApi();
  const { can } = usePortal();
  const p = usePortalItem<ProjectDetail>('projects', id);
  const complete = usePortalMutation((taskId: string) => api.request(`/v1/projects/${encodeURIComponent(id)}/tasks/${encodeURIComponent(taskId)}/complete`, { method: 'POST', body: {} }), { invalidate: ['projects', id], success: 'Task completed' });
  if (p.isLoading) return <Loading />;
  if (p.error || !p.data) return <EmptyState title={p.error?.status === 404 ? 'Project not found' : 'Could not load this project'} hint={p.error?.detail} icon="kanban" action={<Link className="hf-btn" to="/portal/projects">Back</Link>} />;
  const d = p.data;
  const phases = [...(d.phases ?? [])].sort((a, b) => a.order - b.order);
  const tasks = d.tasks ?? [];
  const byPhase = (phaseId: string | null) => tasks.filter((t) => (t.phase_id ?? null) === phaseId);
  const renderTasks = (list: typeof tasks) =>
    list.map((t) => {
      const done = t.status === 'done' || Boolean(t.completed_at);
      const canComplete = !done && t.portal_access === 'complete' && can('projects.tasks.complete');
      return (
        <div key={t.id} className={['pt-task', done && 'done'].filter(Boolean).join(' ')}>
          <Icon name={done ? 'check-circle' : 'circle'} size={16} />
          <span><span className="t">{t.title}</span>{t.due_date && <span className="hf-muted" style={{ fontSize: 'var(--t-12)', marginLeft: 8 }}>due {formatDate(t.due_date)}</span>}</span>
          {canComplete ? <Button size="sm" variant="outline" busy={complete.isPending} onClick={() => complete.mutate(t.id)}>Mark done</Button> : <StatusPill status={t.status} />}
        </div>
      );
    });
  return (
    <div>
      <BackLink to="/portal/projects">Projects</BackLink>
      <PageHeader eyebrow={<span className="hf-mono">{d.ref}</span>} title={d.name} badge={<span style={{ marginLeft: 8 }}><StatusPill status={d.status} /></span>} subtitle={d.description ?? undefined} />
      <ProjectHealth tasks={tasks} prepay={d.prepay ?? []} />
      <div className="hf-stats">
        <div className="hf-stat"><div className="hf-caps">Progress</div><div><Progress value={d.progress_percent ?? 0} /> {d.progress_percent ?? 0}%</div></div>
        {d.start_date && <div className="hf-stat"><div className="hf-caps">Started</div><div>{formatDate(d.start_date)}</div></div>}
        {d.target_end_date && <div className="hf-stat"><div className="hf-caps">Target</div><div>{formatDate(d.target_end_date)}</div></div>}
      </div>
      {phases.map((ph) => (
        <section key={ph.id} className="pt-phase">
          <h2 className="hf-section-title">{ph.name} <small>{ph.start_date ? `${formatDate(ph.start_date)} – ${formatDate(ph.end_date)}` : ''}</small></h2>
          {byPhase(ph.id).length === 0 ? <p className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>No visible tasks.</p> : renderTasks(byPhase(ph.id))}
        </section>
      ))}
      {byPhase(null).length > 0 && (
        <section className="pt-phase">
          <h2 className="hf-section-title">Tasks</h2>
          {renderTasks(byPhase(null))}
        </section>
      )}
      {tasks.length === 0 && phases.length === 0 && <EmptyState title="Nothing to show yet" icon="kanban" />}
    </div>
  );
}

// ── Knowledge base ────────────────────────────────────────────────────────────
/** Articles shown per folder before "Show all" — the landing stays scannable. */
export const KB_FOLDER_TOP = 5;

/** Group articles by folder (category path), folders alphabetical, uncategorised last. */
export function kbFolders(rows: ArticleRow[]): Array<{ folder: string; rows: ArticleRow[] }> {
  const by = new Map<string, ArticleRow[]>();
  for (const r of rows) {
    const k = r.category_path ?? '';
    by.set(k, [...(by.get(k) ?? []), r]);
  }
  return [...by.entries()].sort(([a], [b]) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b))).map(([folder, rs]) => ({ folder, rows: rs }));
}

export function KbPage() {
  const { can } = usePortal();
  const [sp, setSp] = useSearchParams();
  const q = sp.get('q') ?? '';
  const open = sp.get('folder');
  const list = usePortalList<ArticleRow>('kb-articles', { q: q || undefined }, { enabled: can('kb.view'), limit: 200 });
  if (!can('kb.view')) return <EmptyState title="The knowledge base is not available to your portal role" icon="lock" />;
  const setParam = (k: string, v: string | null) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const folders = kbFolders(list.rows).filter((f) => open === null || f.folder === open);
  const card = (a: ArticleRow) => (
    <Link key={a.id} to={`/portal/kb/${a.id}`} className="pt-card">
      <span className="title">{a.title}</span>
      <span className="meta"><span>Updated {formatDate(a.updated_at)}</span></span>
      <span className="side"><Icon name="chevron-right" size={16} /></span>
    </Link>
  );
  return (
    <div>
      <PageHeader title="Knowledge base" subtitle={open !== null ? (open || 'Other articles') : 'Guides and answers'} />
      <div className="pt-toolbar">
        {open !== null && <Button variant="ghost" onClick={() => setParam('folder', null)}><Icon name="chevron-left" size={14} /> All folders</Button>}
        <Input className="pt-search" placeholder="Search articles…" value={q} onChange={(e) => setParam('q', e.target.value)} aria-label="Search articles" />
      </div>
      <ListState loading={list.isLoading} error={list.error} count={list.rows.length} empty={{ title: q ? 'No articles match' : 'No articles yet', icon: 'book-open' }}>
        {folders.map((f) => {
          const shown = open !== null || q ? f.rows : f.rows.slice(0, KB_FOLDER_TOP);
          return (
            <section key={f.folder || '_'} aria-label={f.folder || 'Other articles'}>
              {open === null && <h2 className="hf-section-title">{f.folder || 'Other articles'} <span className="hf-muted">· {f.rows.length}</span></h2>}
              <div className="pt-cards">{shown.map(card)}</div>
              {shown.length < f.rows.length && <Button variant="ghost" onClick={() => setParam('folder', f.folder)}>Show all {f.rows.length}</Button>}
            </section>
          );
        })}
        <LoadMore hasMore={list.hasMore} loading={list.loadingMore} onMore={list.loadMore} />
      </ListState>
    </div>
  );
}

/** "Was this helpful?" — a no asks what was missing (the reason is what makes the signal usable). */
export function ArticleFeedback({ id }: { id: string }) {
  const api = useApi();
  const [mode, setMode] = useState<'ask' | 'why' | 'done'>('ask');
  const [reason, setReason] = useState('');
  const send = usePortalMutation((v: { helpful: boolean; reason?: string }) => api.resource('kb-articles').action('feedback', { id, body: v }), { invalidate: () => {}, onSuccess: () => setMode('done') });
  if (mode === 'done') return <p className="hf-muted" role="status">Thanks — your feedback goes to the people who write these articles.</p>;
  return (
    <section className="pt-feedback" aria-label="Was this article helpful?">
      {mode === 'ask' ? (
        <div className="pt-toolbar">
          <span>Was this helpful?</span>
          <Button onClick={() => send.mutate({ helpful: true })} disabled={send.isPending}><Icon name="thumbs-up" size={14} /> Yes</Button>
          <Button onClick={() => setMode('why')}><Icon name="thumbs-down" size={14} /> No</Button>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); send.mutate({ helpful: false, reason: reason.trim() }); }}>
          <Field label="What was missing or wrong?" required>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} autoFocus />
          </Field>
          <div className="pt-toolbar">
            <Button type="submit" variant="primary" disabled={reason.trim().length < 3 || send.isPending}>Send feedback</Button>
            <Button variant="ghost" onClick={() => setMode('ask')}>Cancel</Button>
          </div>
        </form>
      )}
    </section>
  );
}

export function ArticlePage() {
  const { id = '' } = useParams();
  const api = useApi();
  const a = usePortalItem<ArticleRow>('kb-articles', id);
  if (a.isLoading) return <Loading />;
  if (a.error || !a.data) return <EmptyState title={a.error?.status === 404 ? 'Article not found' : 'Could not load this article'} hint={a.error?.detail} icon="book-open" action={<Link className="hf-btn" to="/portal/kb">Back</Link>} />;
  const d = a.data;
  return (
    <article className="pt-main narrow" style={{ padding: 0 }}>
      <BackLink to="/portal/kb">Knowledge base</BackLink>
      <PageHeader eyebrow={d.category_path ?? undefined} title={d.title} subtitle={`Updated ${formatDate(d.updated_at)}`} />
      <RichTextView html={d.body ?? ''} />
      {(d.attachments ?? []).length > 0 && (
        <div style={{ marginTop: 'var(--s-5)' }}>
          <div className="hf-caps" style={{ marginBottom: 6 }}>Attachments</div>
          <div className="pt-attach">
            {d.attachments!.map((f) => (
              <a key={f.id} href={portalUrl(api, `/v1/attachments/${f.id}/download`)} target="_blank" rel="noopener noreferrer"><Icon name="paperclip" size={14} /><span>{f.filename}</span><small className="hf-muted">{fmtBytes(f.bytes)}</small></a>
            ))}
          </div>
        </div>
      )}
      <ArticleFeedback id={d.id} />
    </article>
  );
}
